/**
 * LinkedIn Insight Tag loader, written out from LinkedIn's install snippet so
 * it can be typed and tested instead of injected as an inline string.
 */

export const LINKEDIN_INSIGHT_SRC =
	"https://snap.licdn.com/li.lms-analytics/insight.min.js";

export type LinkedInTrackPayload = { conversion_id: number };

/**
 * `window.lintrk`. Until insight.min.js loads it is a stub that queues calls
 * on `q`; the script drains the queue when it arrives.
 */
export type Lintrk = ((
	action: "track",
	payload: LinkedInTrackPayload,
) => void) & { q?: unknown[][] };

declare global {
	interface Window {
		lintrk?: Lintrk;
		_linkedin_partner_id?: string;
		_linkedin_data_partner_ids?: string[];
	}
}

/**
 * Registers the partner id, installs the queueing `lintrk` stub and appends
 * insight.min.js. Safe to call more than once: a partner id that is already
 * registered is not added again, and the script is only appended once.
 */
export function installLinkedInInsight(
	win: Window,
	doc: Document,
	partnerId: string,
): void {
	win._linkedin_partner_id = partnerId;
	win._linkedin_data_partner_ids = win._linkedin_data_partner_ids || [];
	if (!win._linkedin_data_partner_ids.includes(partnerId)) {
		win._linkedin_data_partner_ids.push(partnerId);
	}

	if (!win.lintrk) {
		const queue: unknown[][] = [];
		const stub: Lintrk = (action, payload) => {
			queue.push([action, payload]);
		};
		stub.q = queue;
		win.lintrk = stub;
	}

	if (doc.querySelector(`script[src="${LINKEDIN_INSIGHT_SRC}"]`)) {
		return;
	}

	const script = doc.createElement("script");
	script.type = "text/javascript";
	script.async = true;
	script.src = LINKEDIN_INSIGHT_SRC;
	doc.head.appendChild(script);
}
